"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  submitPersonalDetailsRequest,
  updateKycFieldsRequest,
  clearError,
} from "@/store/slices/kyc.slice";
import { formDataUtils } from "@/services/kycService";
import { getUpdatableFieldsFromProblems } from "@/types/models/kyc";
import KYCErrorDisplay from "./KYCErrorDisplay";
import { KYCFieldError, useFieldHasErrors } from "./KYCFieldError";

// Validation Schema - Personal Information (GST)
const personalInfoSchema = z.object({
  gst_number: z.string().min(15, "Please enter a valid GST number"),
});

type PersonalInfoFormData = z.infer<typeof personalInfoSchema>;

interface PersonalInformationStepProps {
  onPrevious: () => void;
  storeId: string;
  kycData?: any | null;
}

export const PersonalInformationStep: React.FC<
  PersonalInformationStepProps
> = ({ onPrevious, storeId, kycData = null }) => {
  const dispatch = useAppDispatch();
  const {
    isLoading,
    error,
    isResubmissionMode,
    rejectedSteps,
    kycProblems,
    rejectionReason,
    kycStatus,
  } = useAppSelector((state) => state.kyc);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Track if form has been submitted to prevent resetting user input on errors
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  // Check if GST field has errors
  const gstHasError = useFieldHasErrors("gst_number", kycProblems);

  // Clear local submit error when Redux error clears (indicates success)
  React.useEffect(() => {
    if (!error && !isLoading) {
      setSubmitError(null);
    }
  }, [error, isLoading]);

  // Clear errors when personal details step is completed successfully
  const isPersonalDetailsCompleted = useAppSelector(
    (state) => state.kyc.completedSteps.personalDetails,
  );
  React.useEffect(() => {
    if (isPersonalDetailsCompleted) {
      setSubmitError(null);
    }
  }, [isPersonalDetailsCompleted]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PersonalInfoFormData>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      gst_number: kycData?.gst_number || "",
    },
  });

  React.useEffect(() => {
    // Only reset form with kycData on initial load, not after user has submitted
    if (kycData && !hasSubmitted) {
      reset({ gst_number: kycData.gst_number || "" });
    }
  }, [kycData, reset, hasSubmitted]);

  const onSubmit = (data: PersonalInfoFormData) => {
    setSubmitError(null);
    dispatch(clearError()); // Clear any previous Redux errors

    // Mark that user has attempted submission to prevent form resets
    setHasSubmitted(true);

    // Use PATCH endpoint for resubmissions, POST for initial submissions
    if (isResubmissionMode && rejectedSteps?.personalDetails) {
      // PATCH: Update only the fields that were actually rejected
      const updatableFields = getUpdatableFieldsFromProblems(kycProblems || []);
      const fields: Record<string, unknown> = {};

      // Only include GST number if it was marked as problematic
      if (updatableFields.includes("gst_number")) {
        fields.gst_number = data.gst_number;
      }

      // Ensure we have at least one field to update
      if (Object.keys(fields).length === 0) {
        setSubmitError(
          "No problematic personal details found to update. Please contact support.",
        );
        return;
      }

      dispatch(
        updateKycFieldsRequest({
          storeId,
          fields,
        }),
      );
    } else {
      // POST: Initial personal details submission
      const personalFormData = formDataUtils.createPersonalDetailsFormData({
        gst_number: data.gst_number,
      });

      dispatch(
        submitPersonalDetailsRequest({
          storeId,
          formData: personalFormData,
        }),
      );
    }

    // Success message - Redux saga will handle step advancement
    console.log(
      "[PersonalInformationStep] Personal details submission dispatched, Redux will handle response and step advancement",
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* KYC Error Display */}
      {kycStatus === "REJECTED" && (kycProblems?.length || rejectionReason) && (
        <div className="mb-6">
          <KYCErrorDisplay
            problems={kycProblems}
            rejectionReason={rejectionReason}
          />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Personal Information
          </h2>
          <p className="text-auth-text-muted">
            Please provide your business registration details
          </p>
        </div>

        {/* Business Registration Number (GST) */}
        <div className="space-y-1">
          <label className="text-auth-text-muted text-sm">
            Business Registration Number <span className="text-red-500">*</span>
          </label>
          <div
            className={`bg-auth-card border rounded-[30px] p-3 ${
              gstHasError
                ? "border-orange-500 bg-orange-50"
                : "border-auth-border"
            }`}
          >
            <input
              {...register("gst_number")}
              className="w-full bg-transparent text-foreground text-base outline-none placeholder:text-muted-foreground placeholder:opacity-60"
              placeholder="Enter GST number (e.g., 22AAAAA0000A1Z5)"
            />
          </div>
          {errors.gst_number && (
            <p className="text-red-500 text-xs">{errors.gst_number.message}</p>
          )}
          <KYCFieldError fieldName="gst_number" problems={kycProblems || []} />
          <p className="text-auth-text-muted text-xs mt-1">
            Enter your 15-digit GST registration number
          </p>
        </div>

        {/* Error Message */}
        {(error || submitError) && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-500 text-sm">{error || submitError}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-6">
          <button
            type="button"
            onClick={onPrevious}
            className="flex items-center space-x-2 px-6 py-3 bg-muted/50 border border-border rounded-[30px] text-muted-foreground hover:text-foreground hover:bg-muted hover:border-muted-foreground/50 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <ChevronDown className="w-5 h-5 rotate-90" />
            <span>Previous</span>
          </button>

          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 active:bg-primary/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl rounded-[30px] transform hover:scale-105 active:scale-95"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{isLoading ? "Submitting..." : "Next"}</span>
            {!isLoading && <ChevronRight className="w-5 h-5" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PersonalInformationStep;
